import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	Paper,
	CircularProgress,
	Alert,
	Collapse,
	IconButton,
} from '@mui/material';
import {
	ExpandMore as ExpandMoreIcon,
	WarningAmber as WarningAmberIcon,
	HelpOutline as HelpOutlineIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	KeyboardArrowUp as KeyboardArrowUpIcon,
	CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface PackageInsightProps {
	snapshotId: string;
}

interface PackageRow {
	id: string;
	package_name: string;
	namespace_prefix: string | null;
	installed_version: string | null;
	latest_version: string | null;
	is_update_available: boolean | null;
	installation_status: string;
	detection_method: string;
	package_classification: string;
	package_category: string | null;
	package_description: string | null;
	confidence: string;
	[key: string]: unknown;
}

// ── Constants ────────────────────────────────────────────────────────────────

const COMMON_COLUMNS = ['package_name', 'namespace_prefix', 'installed_version', 'latest_version'];

const COLUMN_LABELS: Record<string, string> = {
	package_name: 'Package Name',
	namespace_prefix: 'Namespace',
	installed_version: 'Installed Version',
	latest_version: 'Latest Version',
	package_classification: 'Classification',
	detection_method: 'Detection Method',
	confidence: 'Confidence',
};

const CLASSIFICATION_LABELS: Record<string, string> = {
	managed_product: 'Product',
	managed_fusion_internal: 'Internal',
	managed_solution_library: 'Solution Library (Managed)',
	managed_third_party_known: 'Third-Party (Known)',
	managed_third_party_unknown: 'Third-Party (Unknown)',
	unmanaged_third_party_unknown: 'Third-Party Unmanaged (Unknown)',
	unmanaged_accelerator: 'Solution Library (Unmanaged)',
	customer_custom: 'Customer Custom',
	standard_salesforce: 'Standard Salesforce',
};

// ── Inventory section definitions ────────────────────────────────────────────

interface SectionDef {
	key: string;
	label: string;
	filter: (row: PackageRow) => boolean;
	subSections?: { key: string; label: string; filter: (row: PackageRow) => boolean }[];
	columns?: string[];
}

const INVENTORY_SECTIONS: SectionDef[] = [
	{
		key: 'core',
		label: 'Core',
		filter: (r) => r.package_category === 'Core Platform',
	},
	{
		key: 'extensions',
		label: 'Platform Extensions',
		filter: (r) =>
			r.package_classification === 'managed_product' &&
			r.package_category !== 'Core Platform' &&
			r.package_category !== 'Data Connector',
	},
	{
		key: 'connectors',
		label: 'Connectors',
		filter: (r) => r.package_category === 'Data Connector',
	},
	{
		key: 'sla',
		label: 'Solution Library Accelerators',
		filter: (r) =>
			r.package_classification === 'managed_solution_library' ||
			r.package_classification === 'unmanaged_accelerator',
		subSections: [
			{
				key: 'sla_managed',
				label: 'Managed',
				filter: (r) => r.package_classification === 'managed_solution_library',
			},
			{
				key: 'sla_unmanaged',
				label: 'Unmanaged',
				filter: (r) => r.package_classification === 'unmanaged_accelerator',
			},
		],
	},
	{
		key: 'third_party',
		label: 'Third-Party Packages',
		filter: (r) =>
			r.package_classification === 'managed_third_party_known' ||
			r.package_classification === 'managed_third_party_unknown' ||
			r.package_classification === 'unmanaged_third_party_unknown',
		subSections: [
			{
				key: 'tp_known',
				label: 'Known',
				filter: (r) => r.package_classification === 'managed_third_party_known',
			},
			{
				key: 'tp_unknown',
				label: 'Unknown',
				filter: (r) =>
					r.package_classification === 'managed_third_party_unknown' ||
					r.package_classification === 'unmanaged_third_party_unknown',
			},
		],
	},
	{
		key: 'internal',
		label: 'Internal',
		filter: (r) => r.package_classification === 'managed_fusion_internal',
	},
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCell(value: unknown, column: string): string {
	if (value === null || value === undefined) return '\u2014';
	if (column === 'package_classification') {
		return CLASSIFICATION_LABELS[value as string] || String(value);
	}
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	return String(value);
}

// ── Sub-components ───────────────────────────────────────────────────────────

function PackageTable({ rows, columns }: { rows: PackageRow[]; columns: string[] }) {
	if (rows.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
				No packages in this category.
			</Typography>
		);
	}

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						{columns.map((col) => (
							<TableCell key={col} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
								{COLUMN_LABELS[col] || col}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
			<TableBody>
				{rows.map((row) => (
					<TableRow key={row.id} hover>
						{columns.map((col) => (
							<TableCell key={col} sx={{ whiteSpace: col === 'package_name' ? 'normal' : 'nowrap', verticalAlign: 'top' }}>
								{col === 'package_name' ? (
									<Box>
										<Typography variant="body2" component="span">
											{formatCell(row[col], col)}
										</Typography>
										{row.package_description && (
											<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}>
												{row.package_description}
											</Typography>
										)}
									</Box>
								) : col === 'installed_version' && row.is_update_available ? (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										{formatCell(row[col], col)}
										<Chip label="Update available" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
									</Box>
								) : (
									formatCell(row[col], col)
								)}
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
			</Table>
		</TableContainer>
	);
}

interface FindingCardProps {
	severity: 'warning' | 'info';
	icon: React.ReactNode;
	title: string;
	count: number;
	children: React.ReactNode;
}

function FindingCard({ severity, icon, title, count, children }: FindingCardProps) {
	const [expanded, setExpanded] = useState(false);

	if (count === 0) return null;

	const borderColor = severity === 'warning' ? 'warning.main' : 'info.main';

	return (
		<Paper
			variant="outlined"
			sx={{
				mb: 1.5,
				borderLeft: 4,
				borderLeftColor: borderColor,
				overflow: 'hidden',
			}}
		>
			<Box
				onClick={() => setExpanded(!expanded)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					px: 2,
					py: 1.5,
					cursor: 'pointer',
					'&:hover': { backgroundColor: 'action.hover' },
				}}
			>
				{icon}
				<Typography sx={{ fontWeight: 600, ml: 1, flex: 1 }}>
					{title}
				</Typography>
				<Chip
					label={count}
					size="small"
					color={severity}
					sx={{ height: 22, minWidth: 28, mr: 1 }}
				/>
				<IconButton size="small" sx={{ p: 0.25 }}>
					{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</IconButton>
			</Box>
			<Collapse in={expanded}>
				<Box sx={{ borderTop: 1, borderColor: 'divider' }}>
					{children}
				</Box>
			</Collapse>
		</Paper>
	);
}

// ── Main component ───────────────────────────────────────────────────────────

export const PackageInsight: React.FC<PackageInsightProps> = ({ snapshotId }) => {
	const [packages, setPackages] = useState<PackageRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const loadPackages = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const runsResponse = await apiService.getAnalysisRuns(snapshotId);
			if (!runsResponse.success || !runsResponse.data) {
				setError(runsResponse.error || 'Failed to load analysis runs');
				return;
			}

			const completedRuns = runsResponse.data.runs
				.filter((r) => r.status === 'completed' || r.status === 'completed_with_errors')
				.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());

			if (completedRuns.length === 0) {
				setError('No completed analysis runs found. Run an analysis first from the Analysis page.');
				return;
			}

			const resultsResponse = await apiService.getAnalysisResults(completedRuns[0].run_id, 'packages');
			if (!resultsResponse.success || !resultsResponse.data) {
				setError(resultsResponse.error || 'Failed to load package results');
				return;
			}

			const installed = (resultsResponse.data.results as PackageRow[]).filter(
				(r) => r.installation_status === 'installed',
			);
			setPackages(installed);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load package data');
		} finally {
			setLoading(false);
		}
	}, [snapshotId]);

	useEffect(() => {
		loadPackages();
	}, [loadPackages]);

	const behindLatest = useMemo(
		() => packages.filter((r) => r.is_update_available === true),
		[packages],
	);

	const unknownThirdParty = useMemo(
		() => packages.filter(
			(r) =>
				r.package_classification === 'managed_third_party_unknown' ||
				r.package_classification === 'unmanaged_third_party_unknown',
		),
		[packages],
	);

	const inventoryData = useMemo(() => {
		return INVENTORY_SECTIONS.map((section) => {
			const rows = packages.filter(section.filter);
			const subData = section.subSections?.map((sub) => ({
				...sub,
				rows: packages.filter(sub.filter),
			}));
			return { ...section, rows, subData };
		});
	}, [packages]);

	const totalFindings = behindLatest.length + unknownThirdParty.length;

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ mt: 2 }}>
				{error}
			</Alert>
		);
	}

	return (
		<Box>
			<Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
				Installed Packages
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				{packages.length} package{packages.length !== 1 ? 's' : ''} detected across this org.
			</Typography>

			{/* ── Findings ──────────────────────────────────────────────── */}
			<Box sx={{ mb: 3 }}>
				<Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, letterSpacing: 1 }}>
					Findings{totalFindings > 0 ? ` (${totalFindings})` : ''}
				</Typography>

				{totalFindings === 0 ? (
					<Paper
						variant="outlined"
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							px: 2,
							py: 1.5,
							borderLeft: 4,
							borderLeftColor: 'success.main',
						}}
					>
						<CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
						<Typography variant="body2" color="text.secondary">
							No issues found. All packages are up to date and recognized.
						</Typography>
					</Paper>
				) : (
					<>
						<FindingCard
							severity="warning"
							icon={<WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />}
							title="Packages behind latest version"
							count={behindLatest.length}
						>
							<PackageTable
								rows={behindLatest}
								columns={[...COMMON_COLUMNS, 'package_classification']}
							/>
						</FindingCard>

						<FindingCard
							severity="info"
							icon={<HelpOutlineIcon sx={{ color: 'info.main', fontSize: 20 }} />}
							title="Unknown packages"
							count={unknownThirdParty.length}
						>
							<PackageTable
								rows={unknownThirdParty}
								columns={[...COMMON_COLUMNS, 'detection_method', 'confidence']}
							/>
						</FindingCard>
					</>
				)}
			</Box>

			{/* ── Inventory ─────────────────────────────────────────────── */}
			<Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, letterSpacing: 1 }}>
				Package Inventory
			</Typography>

			{inventoryData.map((section) => (
				<Accordion
					key={section.key}
					defaultExpanded={section.rows.length > 0}
					disabled={section.rows.length === 0}
					disableGutters
					sx={{ '&:before': { display: 'none' }, mb: 1 }}
					variant="outlined"
					slotProps={{ transition: { unmountOnExit: true } }}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Typography sx={{ fontWeight: 600 }}>
								{section.label}
							</Typography>
							<Chip
								label={section.rows.length}
								size="small"
								sx={{ ml: 1.5, height: 22, minWidth: 28 }}
								variant="filled"
							/>
						</Box>
					</AccordionSummary>
					<AccordionDetails sx={{ p: 0 }}>
						{section.subData ? (
							section.subData.map((sub) => (
								<Box key={sub.key}>
									<Typography
										variant="subtitle2"
										sx={{ px: 2, pt: 2, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}
									>
										{sub.label} ({sub.rows.length})
									</Typography>
									<PackageTable
										rows={sub.rows}
										columns={section.columns || COMMON_COLUMNS}
									/>
								</Box>
							))
						) : (
							<PackageTable
								rows={section.rows}
								columns={section.columns || COMMON_COLUMNS}
							/>
						)}
					</AccordionDetails>
				</Accordion>
			))}
		</Box>
	);
};
